<template>
  <div>
    <div class="p-0.5 mb-2 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Status</label>
        <div style="width: 180px;">
          <SimpleMultiSelect
            v-model="statusFilter"
            :options="statusFilterOptions"
            :sensitive-labels="false"
            placeholder="Active"
            @change="tableKey++"
          />
        </div>
      </div>

      <!-- Bulk Actions Toolbar -->
      <div v-if="selectedTasks.length > 0" class="flex items-center gap-2 bg-orange-50 px-4 py-2 rounded-md">
        <span class="text-sm font-medium text-gray-700">
          {{ selectedTasks.length }} task{{ selectedTasks.length !== 1 ? 's' : '' }} selected
        </span>
        <button
          @click="showAddToCampaignModal = true"
          class="btn-primary-sm"
        >
          Add to Campaign
        </button>
        <button
          @click="clearSelection"
          class="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
        >
          Clear Selection
        </button>
      </div>
    </div>

    <div v-if="portal_roles" class="h-auto grid grid-cols-5 rounded-md shadow-[0px_0px_0px_1px_rgba(9,9,11,0.07),0px_2px_2px_0px_rgba(9,9,11,0.05)]">
      <div data-private class="" :class="sidepanelOpen ? 'col-span-3 h-full' : 'col-span-5 h-full'"  >
        <AgGridVue
          :key="tableKey"
          :theme="taskListTheme"
          style="height: 900px"
          :column-defs="computedColumnDefs"
          :default-col-def="defaultColDef"
          :group-display-type="groupDisplayType"
          :grid-options="gridOptions"
          :loading="loading"
          :rowHeight="180"
          :row-model-type="'serverSide'"
          :row-selection="rowSelection"
          :selection-column-def="selectionColumnDef"
          :get-row-id="getRowId"
          @grid-ready="onGridReady"
          @row-clicked="onRowClicked"
          @selection-changed="onSelectionChanged"
        />
      </div>
      <div v-if="sidepanelOpen" style="height: 900px" class=" col-span-2 overflow-auto">
        <Task_Side_Panel
          :key="sidePanelKey"
          :row="selectedRow"
          @close="onClose"
          @update_row="onUpdateRow"
          @submit="onSubmit"
        />
      </div>
    </div>

    <!-- Add to Campaign Modal -->
    <AddToCampaignModal
      :is-open="showAddToCampaignModal"
      :selected-tasks="selectedTasks"
      @close="showAddToCampaignModal = false"
      @success="onCampaignSuccess"
    />
  </div>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash/debounce';
import { AgGridVue } from 'ag-grid-vue3';
import { themeQuartz } from 'ag-grid-enterprise';
import Task_Side_Panel from '@/components/Portal/Task_Tables/Task_Side_Panel.vue'
import AddToCampaignModal from '@/components/Portal/Task_Tables/AddToCampaignModal.vue'
import {mapGetters} from 'vuex';
import CC_Tag_Badge from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Tag_Badge.vue';
import CC_Priority_Column_Generic from '@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Priority_Column_Generic.vue';
import {toProperCase} from '@/utils/common';
import { sensitiveSetFilterParams } from '@/utils/demoSpoof';
import { getTaskFilterValues } from '@/services/api/tasks';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import CC_Work_Summary_Column from '@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Work_Summary_Column.vue';
import CC_Patient_Composite_Renderer
  from "@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Patient_Composite_Renderer.vue";
import CC_Patient_Header_Component
  from "@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Patient_Header_Component.vue";
import CC_Payer_Header_Component
  from "@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Payer_Header_Component.vue";
import CC_Payer_Composite_Renderer
  from "@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Payer_Composite_Renderer.vue";
import CC_Extra_Info_Composite_Renderer
  from "@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Extra_Info_Composite_Renderer.vue";
import CC_Extra_Info_Header_Component
  from "@/components/Portal/Task_Tables/Custom_Cell_Renderers/CC_Extra_Info_Header_Component.vue";

export default {
  components: {
    AgGridVue,
    Task_Side_Panel,
    AddToCampaignModal,
    SimpleMultiSelect,
    CC_Priority_Column_Generic,
    CC_Work_Summary_Column,
    CC_Patient_Composite_Renderer,
    CC_Patient_Header_Component,
    CC_Payer_Composite_Renderer,
    CC_Payer_Header_Component,
    CC_Extra_Info_Composite_Renderer,
    CC_Extra_Info_Header_Component,
  },
  props: {
    entity:{
      type:Object,
      required:false,
      default: null,
    },
    isUnifiedWorklist:{
      type: Boolean,
      required: false,
      default: false,
    },
    portal_roles:{
      type:Array,
      required:true,
      default: () => []
    },
    autoHeight: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialTaskTypes: {
      type: Array,
      required: false,
      default: () => [],
    },
    initialStatusFilter: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      rowSelection : {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: false,
        enableClickSelection: false,
      },
      selectionColumnDef: {
        width: 50,
        suppressHeaderMenuButton: true,
        lockPosition: true,
        cellStyle: {
          display: 'flex',
          border: 'none'
        },
        cellClass: 'checkbox-cell',
      },
      selectedRow: null,
      selectedRowIndex: null,
      selectedTasks: [],
      showAddToCampaignModal: false,
      sidePanelKey:0,
      sidepanelOpen:false,
      statusFilter: [],
      gridApi: null,
      gridColumnApi: null,
      gridOptions: {
        rowModelType: 'serverSide',
        maxConcurrentDatasourceRequests: 2,
        suppressAggFuncInHeader: true,
        domLayout:this.autoHeight ? 'autoHeight':null,
        context:{
          componentParent: this,
          onPatientSort: this.onPatientSort.bind(this),
          onPatientFilter: this.onPatientFilter.bind(this),
          onPatientClearFilters: this.onPatientClearFilters.bind(this),
          onPayerSort: this.onPayerSort.bind(this),
          onPayerFilter: this.onPayerFilter.bind(this),
          onPayerClearFilters: this.onPayerClearFilters.bind(this),
          onExtraInfoSort: this.onExtraInfoSort.bind(this),
          onExtraInfoFilter: this.onExtraInfoFilter.bind(this),
          onExtraInfoClearFilters: this.onExtraInfoClearFilters.bind(this)
        }
      },
      loading: false,
      groupDisplayType: 'singleColumn',
      defaultColDef: {
        filter: true,
        sortable: false,
        resizable: true,
        cellClass: 'cell-theme-left-aligned',
        filterParams: {
          debounceMs: 1000,
        },
      },
      tableKey: 0,
      dashboardKey:0,
      isOpenDeleteActionModal: false,
      isOpenCompleteActionModal: false,
      completeActionId: null,
      isActionComplete: false,
      deleteActionId: null,
      filters:{},
      taskListTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        headerBackgroundColor: '#8080800A',
        headerFontSize: 12,
        fontSize: 12,
        rowBorder: true,
        wrapperBorder: false,
      }),
      customFilters: {
        patient: {
          patientId: '',
          lastName: '',
          firstName: '',
          pcp: ''
        },
        payer: {
          payerName: '',
          contractType: '',
          contractName: [], // Change from string to array
          contractState: ''
        },
        extraInfo: {
          contractedMeasureName: [], // Change from string to array
          pharmacy: ''
        }
      },
      // Set again on mounted.
      debouncedRefreshGrid() { this.refreshGrid(); },
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('entity', [
      'availableEntities',
      'current_entity_id',
      'current_entity_name',
    ]),
    statusFilterOptions() {
      return [
        { value: '1', label: 'Active' },
        { value: '7', label: 'On Hold' },
        { value: '2', label: 'Closed Out' },
        { value: '3', label: 'Completed' },
        { value: '5', label: 'Skipped' },
        { value: '6', label: 'Expired' },
      ];
    },
    computedColumnDefs() {
      const columns = [];

      // Add practice name column at the beginning if unified worklist
      columns.push(
        {
        headerName: 'Practice',
        headerTooltip: 'Practice Name',
        field: 'metadata.practice_name',
        cellClass: 'cell-theme-left-aligned sensitive',
        hide: !this.isUnifiedWorklist,
        filter: 'agSetColumnFilter',
        sortable: true,
        filterParams: {
          ...sensitiveSetFilterParams,
          values: this.filterValues('practice_name'),
          defaultToNothingSelected: true,
          buttons: ['clear', 'apply'],
        }
      },
      {
        headerName: 'Payer',
        field: 'payer_composite',
        cellRenderer: CC_Payer_Composite_Renderer,
        context: { demoSensitive: true },
        cellClass: 'cell-theme-left-aligned',
        filter: false,
        hide: !this.isUnifiedWorklist,
        minWidth: 200,
        valueGetter: params => {
          if (!params.data) return null;
          return {
            payer_name: params.data.metadata?.payer_name,
            contract_type: params.data.metadata?.contract_type,
            contract_name: params.data.metadata?.contract_name,
            contract_state: params.data.metadata?.contract_state
          };
        },
        headerComponent: CC_Payer_Header_Component,
      },
      {
        headerName: 'Hidden Payer Name',
        field: 'task.payer_name',
        hide: true,
        sortable: true,
      },
      {
        headerName: 'Hidden Contract Type',
        field: 'task.contract_type',
        hide: true,
        sortable: true,
      },
      {
        headerName: 'Hidden Contract Name',
        field: 'task.contract_name',
        hide: true,
        sortable: true,
      },
      {
        headerName: 'Hidden Contract State',
        field: 'task.contract_state',
        hide: true,
        sortable: true,
      },
      {
        headerName: 'Task Name',
        headerTooltip: 'Task Name',
        field: 'task_type_name',
        cellClass:'cell-theme-left-aligned font-semibold',
        wrapText: false,
        filter: 'agSetColumnFilter',
        sortable: false,
        filterParams: {
          values: this.filterValues('task_type_name'),
          defaultToNothingSelected: true,
          buttons: ['clear', 'apply'],
        }
      },
      {
        headerName: 'Status',
        field:'task_status_name',
        maxWidth:150,
        headerTooltip: 'Task Status',
        valueGetter: params => {
          if (params.data && params.data.task_status_name) {
            return toProperCase(params.data.task_status_name);
          }
          return '';
        },
        cellClass:'cell-theme-left-aligned',
        cellRenderer: CC_Tag_Badge,
        cellRendererParams: {
          getBlockColor: function(params) {
            if (!params.data) {return '#E9E9E9'}
            switch(params.data.task_status_id) {
            case 0:
              return '#C0C0C0';
            case 1:
              return '#ABCED6';
            case 2:
              return '#FF968A';
            case 3:
              return '#97C1A9';
            case 4:
              return '#F5E6AB';
            default:
              return '#F9F9F9';
            }
          }
        },
        filter: 'agSetColumnFilter',
        sortable: false,
        filterParams: {
          values: this.filterValues('task_status_name'),
          defaultToNothingSelected: true,
          buttons: ['clear', 'apply'],
        }
      },
      {
        headerName: 'Owner',
        headerTooltip: 'Owner',
        cellClass: 'cell-theme-left-aligned sensitive',
        maxWidth:150,
        field: 'task_owner.task_owner_name',
        colId: 'task_owner_name',
        hide: !this.isUnifiedWorklist,
        filter: 'agSetColumnFilter',
        sortable: false,
        filterParams: {
          ...sensitiveSetFilterParams,
          values: this.filterValues('task_owner_name'),
          defaultToNothingSelected: true,
          buttons: ['clear', 'apply'],
        }
      },
      {
        headerName: 'Flag',
        headerTooltip: 'QA Returned / Coder Response',
        colId: 'reassignment_flag',
        maxWidth: 160,
        cellClass: 'cell-theme-left-aligned',
        valueGetter: params => {
          const rt = params.data?.metadata?.reassignment_type;
          if (rt === 'qa_returned') return 'QA Returned';
          if (rt === 'coder_response') return 'Coder Response';
          return '';
        },
        filter: 'agSetColumnFilter',
        sortable: true,
        filterParams: {
          values: ['QA Returned', 'Coder Response'],
          defaultToNothingSelected: true,
          buttons: ['clear', 'apply'],
        }
      },
      {
        headerName: 'Extra Info',
        field: 'extra_info_composite',
        cellRenderer: CC_Extra_Info_Composite_Renderer,
        context: { demoSensitive: true },
        cellClass: 'cell-theme-left-aligned',
        filter: false,
        minWidth: 220,
        valueGetter: params => {
          if (!params.data) return null;
          return params.data.metadata.extra_info || {};
        },
        headerComponent: CC_Extra_Info_Header_Component,
      },
      {
        field: 'metadata.next_appointment_date',
        hide: true,
        sortable: true,
      },
      {
        headerName: 'Hidden Contracted Measure Name',
        field: 'metadata.contracted_measure_name',
        hide: true,
        sortable: true,
      },
      {
        headerName: 'Hidden Pharmacy',
        field: 'metadata.pharmacy',
        hide: true,
        sortable: true,
      },
      {
        field: 'metadata.payer_name',
        hide: true,
        sortable: true,
      },
      {
        field: 'metadata.contract_type',
        hide: true,
        sortable: true,
      },
      {
        field: 'metadata.contract_name',
        hide: true,
        sortable: true,
      },
      {
        field: 'metadata.contract_state',
        hide: true,
        sortable: true,
      },
      {
        headerName: 'Patient',
        field: 'patient_composite',
        cellRenderer: CC_Patient_Composite_Renderer,
        context: { demoSensitive: true },
        cellClass: 'cell-theme-left-aligned',
        hide: false,
        minWidth:200,
        filter: false,
        valueGetter: params => {
          if (!params.data) return null;
          return {
            patient_identifier: params.data.metadata?.patient_identifier,
            bene_last_name: params.data.metadata?.bene_last_name,
            bene_first_name: params.data.metadata?.bene_first_name,
            bene_dob: params.data.metadata?.bene_dob,
            bene_pcp: params.data.metadata?.bene_pcp
          };
        },
        headerComponent: CC_Patient_Header_Component,
      },
      {
        headerName: 'Work Summary',
        field: 'work_summary',
        cellClass: 'cell-theme-left-aligned',
        filter: false,
        minWidth:200,
        sortable: false,
        cellRenderer: CC_Work_Summary_Column,
        cellRendererParams: {
        }
      },
      {
        headerName: 'Priority',
        field: 'task_valid_thru',
        cellClass: 'cell-theme-left-aligned',
        hide: !this.isUnifiedWorklist,
        filter: true,
        sortable: true,
        autoHeight: true,
        minWidth: 200,
        cellRenderer: CC_Priority_Column_Generic,
        valueGetter: params => {
          // For sorting/filtering, return the task_valid_thru value
          return params.data?.valid_thru;
        },
        cellRendererParams: (params) => ({  // Changed to arrow function
          metricId: params.data.opportunities[0].metric || null, // Pass metric_id here
          // Pass the priority reasons data to the renderer
          getValue: params => {
            if (!params.data?.metadata?.priority_reasons) return null;
            return {
              dbts_lastdaytofail: params.data.metadata.priority_reasons.dbts_lastdaytofail,
              ace_lastdaytofail: params.data.metadata.priority_reasons.ace_lastdaytofail,
              statn_lastdaytofail: params.data.metadata.priority_reasons.statn_lastdaytofail
            };
          }
        })
      },
      {
        headerName: 'Priority',
        hide:this.isUnifiedWorklist,
        field: 'priority_score',
        cellClass:'cell-theme-center-aligned',
        headerTooltip: 'Aggregate priority score from linked opportunities',
        valueGetter: params => parseFloat(params.data?.priority_score),
        valueFormatter: params => {
          if (params.value === null || params.value === undefined) {
            return '';
          }
          return params.value;
        },
      }
      );

      return columns;
    }
  },
  methods: {
    getRowId(params){
      return params.data.id;
    },

    onSelectionChanged() {
      if (!this.gridApi) return;
      const selectedNodes = this.gridApi.getSelectedNodes();
      this.selectedTasks = selectedNodes.map(node => node.data);
    },

    clearSelection() {
      if (!this.gridApi) return;
      this.gridApi.deselectAll();
      this.selectedTasks = [];
    },

    onCampaignSuccess() {
      this.clearSelection();
      this.refreshGrid();
    },

    // Helper to determine sort type
    getSortType(field) {
      if (field.includes('dob') || field.includes('date') || field.includes('_dt') || field.includes('lastdaytofail')) {
        return 'date';
      }
      if (field.includes('score') || field.includes('count') || field.includes('amount')) {
        return 'number';
      }
      // Add handling for our metadata fields
      if (field.includes('metadata.contracted_measure_name') || field.includes('metadata.pharmacy')) {
        return 'string';
      }
      return 'string';
    },

    // Patient handlers - now use AG-Grid API
    onPatientSort(sortConfig) {
      if (sortConfig) {
        // Apply sort through AG-Grid API so it appears in params.request
        this.gridApi.applyColumnState({
          state: [{
            colId: sortConfig.field,
            sort: sortConfig.direction
          }],
          defaultState: { sort: null }
        });
      } else {
        // Clear sorts
        this.gridApi.applyColumnState({
          defaultState: { sort: null }
        });
      }

      this.refreshGrid();
    },

    // Patient filter handlers - use AG-Grid's filter API
    onPatientFilter(filters) {
      this.loading = true;
      this.gridApi.setGridOption('loading', true);

      // Update the custom filter state
      this.customFilters.patient = {
        patientId: filters.patientId || '',
        lastName: filters.lastName || '',
        firstName: filters.firstName || '',
        pcp: filters.pcp || ''
      };

      // Trigger debounced grid refresh
      this.debouncedRefreshGrid();
    },

    onPatientClearFilters() {
      // Clear patient filters
      this.customFilters.patient = {
        patientId: '',
        lastName: '',
        firstName: '',
        pcp: ''
      };

      this.refreshGrid(true);
    },

    // Payer filter handlers - use correct AG-Grid filter API
    onPayerFilter(filters) {
      // Show loading state
      this.loading = true;
      this.gridApi.setGridOption('loading', true);

      // Update the custom filter state
      this.customFilters.payer = {
        payerName: filters.payerName || '',
        contractType: filters.contractType || '',
        contractName: filters.contractName || [], // Handle as array
        contractState: filters.contractState || ''
      };

      // Trigger debounced grid refresh
      this.debouncedRefreshGrid();
    },

    onPayerClearFilters() {
      // Clear payer filters
      this.customFilters.payer = {
        payerName: '',
        contractType: '',
        contractName: [], // Reset to empty array
        contractState: ''
      };

      this.refreshGrid(true);
    },

    // Payer handlers - now use AG-Grid API
    onPayerSort(sortConfig) {
      if (sortConfig) {
        // Apply sort through AG-Grid API
        this.gridApi.applyColumnState({
          state: [{
            colId: sortConfig.field,
            sort: sortConfig.direction
          }],
          defaultState: { sort: null }
        });
      } else {
        // Clear sorts
        this.gridApi.applyColumnState({
          defaultState: { sort: null }
        });
      }

      this.refreshGrid();
    },

    // Extra Info handlers - use AG-Grid API
    onExtraInfoSort(sortConfig) {
      if (sortConfig) {
        // Apply sort through AG-Grid API
        this.gridApi.applyColumnState({
          state: [{
            colId: sortConfig.field,
            sort: sortConfig.direction
          }],
          defaultState: { sort: null }
        });
      } else {
        // Clear sorts
        this.gridApi.applyColumnState({
          defaultState: { sort: null }
        });
      }

      this.refreshGrid();
    },

    onExtraInfoFilter(filters) {
      this.loading = true;
      this.gridApi.setGridOption('loading', true);

      // Update the custom filter state
      this.customFilters.extraInfo = {
        contractedMeasureName: filters.contractedMeasureName || [], // Change to array
        pharmacy: filters.pharmacy || ''
      };

      // Trigger debounced grid refresh
      this.debouncedRefreshGrid();
    },

    onExtraInfoClearFilters() {
      // Clear extra info filters
      this.customFilters.extraInfo = {
        contractedMeasureName: [], // Change from string to array
        pharmacy: ''
      };

      this.refreshGrid(true);
    },

    refreshGrid(purge=false) {
      if (this.gridApi) {
        // Set loading to true which will show the overlay
        this.loading = true;
        this.gridApi.setGridOption('loading', true);

        // Then refresh
        this.gridApi.refreshServerSide({ purge: purge });
      }
    },

    // Set-filter values load when a column's filter is first opened, one column per
    // request: computing every dropdown on mount scanned all of a tenant's tasks
    // (ticket 3969). Results are cached for the life of the component.
    filterValues(field) {
      return async (params) => {
        try {
          if (!this.filters[field]) {
            const values = await getTaskFilterValues({ fields: [field] });
            this.filters[field] = values[field] || [];
          }
          params.success(this.filters[field]);
        } catch (error) {
          console.error('Error fetching filters:', error);
          params.success([]);
        }
      };
    },

    async onGridReady(params) {
      this.gridApi = params.api;

      const datasource = {
        getRows: async (paramsRequest) => {

          // Status filter: user-selected or default to Active + On Hold
          if (!paramsRequest.request.filterModel) {
            paramsRequest.request.filterModel = {};
          }
          const statuses = this.statusFilter.length > 0 ? this.statusFilter : ['1', '7'];
          paramsRequest.request.filterModel['status'] = {
            filterType: 'set',
            values: statuses
          };

          // Build custom filter model by merging AG-Grid filters with custom filters
          const customFilterModel = {};

          // Add patient filters if they have values
          if (this.customFilters.patient.patientId) {
            customFilterModel['metadata.patient_identifier'] = {
              filterType: 'text',
              type: 'contains',
              filter: this.customFilters.patient.patientId
            };
          }
          if (this.customFilters.patient.lastName) {
            customFilterModel['metadata.bene_last_name'] = {
              filterType: 'text',
              type: 'contains',
              filter: this.customFilters.patient.lastName
            };
          }
          if (this.customFilters.patient.firstName) {
            customFilterModel['metadata.bene_first_name'] = {
              filterType: 'text',
              type: 'contains',
              filter: this.customFilters.patient.firstName
            };
          }
          if (this.customFilters.patient.pcp) {
            customFilterModel['metadata.bene_pcp'] = {
              filterType: 'text',
              type: 'contains',
              filter: this.customFilters.patient.pcp
            };
          }

          // Add payer filters if they have values
          if (this.customFilters.payer.payerName) {
            customFilterModel['metadata.payer_name'] = {
              filterType: 'text',
              type: 'contains',
              filter: this.customFilters.payer.payerName
            };
          }
          if (this.customFilters.payer.contractType) {
            customFilterModel['metadata.contract_type'] = {
              filterType: 'text',
              type: 'contains',
              filter: this.customFilters.payer.contractType
            };
          }
          if (this.customFilters.payer.contractName && this.customFilters.payer.contractName.length > 0) {
            customFilterModel['metadata.contract_name'] = {
              filterType: 'set',  // Change from 'text' to 'set'
              values: this.customFilters.payer.contractName  // Pass the array directly
            };
          }
          if (this.customFilters.payer.contractState) {
            customFilterModel['metadata.contract_state'] = {
              filterType: 'text',
              type: 'contains',
              filter: this.customFilters.payer.contractState
            };
          }

          // Add extra info filters if they have values
          if (this.customFilters.extraInfo.contractedMeasureName && this.customFilters.extraInfo.contractedMeasureName.length > 0) {
            customFilterModel['metadata.contracted_measure_name'] = {
              filterType: 'set',  // Change from 'text' to 'set'
              values: this.customFilters.extraInfo.contractedMeasureName  // Pass the array directly
            };
          }
          if (this.customFilters.extraInfo.pharmacy) {
            customFilterModel['metadata.pharmacy'] = {
              filterType: 'text',
              type: 'contains',
              filter: this.customFilters.extraInfo.pharmacy
            };
          }

          // Merge custom filters with AG-Grid's filter model
          const mergedFilterModel = {
            ...paramsRequest.request.filterModel,
            ...customFilterModel
          };

          // Build sort model - now ALL sorts come through paramsRequest
          let sortModel = [];

          if (paramsRequest.request.sortModel && paramsRequest.request.sortModel.length > 0) {
            // User has sorted - use their sorts
            sortModel = paramsRequest.request.sortModel.map(sort => ({
              colId: sort.colId,
              sort: sort.sort,
              sort_type: this.getSortType(sort.colId)
            }));
          } else {
            // No user sorts - use defaults with priority_score first
            sortModel.push({
              colId: 'metadata__priority_score',
              sort: 'desc',
              sort_type: 'number',
            });
            if (!this.isUnifiedWorklist) {
              sortModel.push(
                {
                  colId: 'metadata__patient_identifier',
                  sort: 'desc',
                  sort_type: 'string',
                },
                {
                  colId: 'metadata__clm_thru_dt',
                  sort: 'asc',
                  sort_type: 'date'
                },
                {
                  colId: 'metadata__pharmacy',
                  sort: 'asc',
                  sort_type: 'string'
                }
              );
            }
          }

          try {
            const response = await axios.post('api/tasks/worklist/',
              {
                start_row: paramsRequest.request.startRow,
                end_row: paramsRequest.request.endRow,
                filterModel: mergedFilterModel,
                sortModel: sortModel,
                taskTypes: this.initialTaskTypes,
                entityId: this.entity,
              }
            );
            if (response.data?.rowData) {
              paramsRequest.success({
                rowData: response.data.rowData });
            } else {
              paramsRequest.success({
                rowData: []
              });
            }

            // Set loading to false when done
            this.loading = false;
            this.gridApi.setGridOption('loading', false);

            params.api.sizeColumnsToFit();
          } catch (error) {
            console.error('Error fetching data:', error);
            paramsRequest.fail();

            // Also set loading to false on error
            this.loading = false;
            this.gridApi.setGridOption('loading', false);
          }
        },
      };

      this.gridApi.setGridOption('serverSideDatasource', datasource);
      window.addEventListener('resize', this.onWindowResize);
    },

    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    },

    onRowClicked(event) {
      this.selectedRow = event.data;
      this.selectedRowIndex = event.node.rowIndex;
      this.sidePanelKey += 1;
      this.sidepanelOpen = true;
    },

    onClose(){
      this.sidepanelOpen = false;
    },

    onUpdateRow(){
      this.gridApi.refreshServerSide();
    },

    onSubmit() {
      if (this.gridApi && this.selectedRowIndex !== null) {
        const nextIndex = this.selectedRowIndex + 1;
        const rowCount = this.gridApi.getDisplayedRowCount();

        if (nextIndex < rowCount) {
          const nextRowNode = this.gridApi.getDisplayedRowAtIndex(nextIndex);
          if (nextRowNode) {
            this.gridApi.deselectAll();
            nextRowNode.setSelected(true);
            this.selectedRow = nextRowNode.data;
            this.selectedRowIndex = nextRowNode.rowIndex;
          }
        } else {
          this.gridApi.deselectAll();
          this.selectedRow = null;
          this.selectedRowIndex = null;
        }
      }

      this.$emit('submit');
      this.gridApi.refreshServerSide();
      this.sidePanelKey += 1;
    },

  },

  watch: {
    sidepanelOpen() {
      setTimeout(() => {
        this.onWindowResize();
      }, 100);
    },
    initialTaskTypes: {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue && oldValue.length > 0) {
          this.gridApi?.refreshServerSide({ purge: true });
        }
      },
    },
    initialStatusFilter: {
      deep: true,
      handler(newValue) {
        this.statusFilter = newValue || [];
        this.gridApi?.refreshServerSide({ purge: true });
      },
    },
  },

  mounted() {
    this.debouncedRefreshGrid = debounce(() => this.refreshGrid(), 1000);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },
};
</script>

<style>

</style>
